import { useState, useEffect } from 'react';
import { Trash2, AlertTriangle, X, Database, Save, GitPullRequest } from 'lucide-react';
import { Project } from '../types';
import { apiService } from '../services/api';
import GitRepoConnect from './GitRepoConnect';

interface ProjectSettingsModalProps {
  project: Project | null;
  isOpen: boolean;
  onClose: () => void;
  onProjectDeleted: () => void;
}

export default function ProjectSettingsModal({
  project,
  isOpen,
  onClose,
  onProjectDeleted
}: ProjectSettingsModalProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [retentionDays, setRetentionDays] = useState(30);
  const [isSavingRetention, setIsSavingRetention] = useState(false);
  const [retentionSaved, setRetentionSaved] = useState(false);
  const [autoOpenPR, setAutoOpenPR] = useState(false);
  const [isSavingAutoPR, setIsSavingAutoPR] = useState(false);
  const [autoPRSaved, setAutoPRSaved] = useState(false);

  useEffect(() => {
    if (isOpen && project) {
      setRetentionDays(project.sessionRetentionDays || 30);
      setAutoOpenPR(project.approvalSettings?.autoOpenPR === true);
      setRetentionSaved(false);
      setAutoPRSaved(false);
    }
  }, [isOpen, project]);

  // Storage-stats fetch was here. The backend route /projects/:id/storage-stats
  // was never registered, so every modal open hit a silent 404 and the panel
  // rendered empty. Removed the fetch + the render block until the route ships;
  // restore by re-adding loadStorageStats and re-mounting the storage block.

  if (!isOpen || !project) {
    return null;
  }

  const handleSaveRetention = async () => {
    try {
      setIsSavingRetention(true);
      const response = await apiService.updateProject(project._id, { sessionRetentionDays: retentionDays });
      if (response.success) {
        setRetentionSaved(true);
        setTimeout(() => setRetentionSaved(false), 3000);
      } else {
        alert('Failed to update retention: ' + (response.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Failed to update retention:', error);
      alert('Failed to update retention period. Please try again.');
    } finally {
      setIsSavingRetention(false);
    }
  };

  const handleToggleAutoOpenPR = async (next: boolean) => {
    if (!project) return;
    try {
      setIsSavingAutoPR(true);
      const response = await apiService.updateProject(project._id, {
        approvalSettings: {
          ...(project.approvalSettings || {}),
          autoOpenPR: next,
        },
      });
      if (response.success) {
        setAutoOpenPR(next);
        setAutoPRSaved(true);
        setTimeout(() => setAutoPRSaved(false), 3000);
      } else {
        alert('Failed to update auto-PR setting: ' + (response.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Failed to update auto-PR:', error);
      alert('Failed to update auto-PR setting. Please try again.');
    } finally {
      setIsSavingAutoPR(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!confirm(`Are you sure you want to delete "${project.name}"? This action cannot be undone and will permanently delete all associated session data.`)) {
      return;
    }

    try {
      setIsDeleting(true);
      const response = await apiService.deleteProject(project._id);

      if (response.success) {
        alert('Project deleted successfully!');
        onProjectDeleted();
        onClose();
      } else {
        alert('Failed to delete project: ' + (response.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Failed to delete project:', error);
      alert('Failed to delete project. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const hasRetentionChanged = retentionDays !== (project.sessionRetentionDays || 30);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Project settings</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Project information</h3>
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-gray-500">Name</dt>
                <dd className="text-gray-900 font-medium">{project.name}</dd>
              </div>
              {project.description && (
                <div>
                  <dt className="text-gray-500">Description</dt>
                  <dd className="text-gray-900">{project.description}</dd>
                </div>
              )}
              <div>
                <dt className="text-gray-500">Created</dt>
                <dd className="text-gray-900">
                  {new Date(project.createdAt).toLocaleDateString()}
                </dd>
              </div>
            </dl>
          </div>

          {/* Storage & Retention */}
          <div className="border-t border-gray-200 pt-6 mb-6">
            <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
              <Database className="h-4 w-4 mr-2" />
              Storage & Retention
            </h3>

            {/* Storage stats panel + expiry warning lived here. The
                /projects/:id/storage-stats route was never registered
                backend-side, so the panel rendered empty in production.
                Both removed until the route ships. */}

            {/* Retention Configuration */}
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label htmlFor="retentionDays" className="block text-sm text-gray-600 mb-1">
                  Retention period (days)
                </label>
                <input
                  id="retentionDays"
                  type="number"
                  min={1}
                  max={365}
                  value={retentionDays}
                  onChange={(e) => setRetentionDays(Math.max(1, Math.min(365, parseInt(e.target.value) || 1)))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm px-3 py-2 border"
                />
              </div>
              <button
                onClick={handleSaveRetention}
                disabled={!hasRetentionChanged || isSavingRetention}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="h-4 w-4 mr-1" />
                {isSavingRetention ? 'Saving...' : 'Save'}
              </button>
            </div>
            {retentionSaved && (
              <p className="mt-2 text-sm text-green-600">Retention period updated successfully.</p>
            )}
            <p className="mt-2 text-xs text-gray-400">
              Sessions older than this will be automatically deleted. Min: 1 day, max: 365 days.
            </p>
          </div>

          {/* Git Repository */}
          <div className="border-t border-gray-200 pt-6 mb-6">
            <GitRepoConnect projectId={project._id} />
          </div>

          {/* Approval Automation */}
          <div className="border-t border-gray-200 pt-6 mb-6">
            <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
              <GitPullRequest className="h-4 w-4 mr-2" />
              Approval Automation
            </h3>
            <label className="flex items-start cursor-pointer">
              <input
                type="checkbox"
                checked={autoOpenPR}
                disabled={isSavingAutoPR}
                onChange={(e) => handleToggleAutoOpenPR(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
              />
              <span className="ml-2 text-sm">
                <span className="font-medium text-gray-900">Automatically open a PR when a variant is approved</span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  Requires a connected Git repository. Code-gen + PR creation runs in the background; failures are logged but never roll back the approval.
                </span>
              </span>
            </label>
            {autoPRSaved && (
              <p className="mt-2 text-sm text-green-600">Auto-PR setting updated.</p>
            )}
          </div>

          {/* DetectionSettingsPanel was here. Its three apiService calls
              (getDetectionSettings / updateDetectionSettings /
              resetDetectionSettings) all 404 the backend — the routes
              were never registered, only the Mongo model exists. Hidden
              until the routes ship; the component file (DetectionSettingsPanel)
              is still in the tree so the design work doesn't have to be
              re-done. */}

          {/* Danger Zone */}
          <div className="border-t border-gray-200 pt-6">
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-semibold text-red-800">Danger zone</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p className="mb-4">
                      This action cannot be undone. This will permanently delete the project and all associated session data.
                    </p>
                    <button
                      onClick={handleDeleteProject}
                      disabled={isDeleting}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {isDeleting ? 'Deleting...' : 'Delete Project'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
